/**
 * In-memory storage adapter for Express
 * Useful for development and testing
 */

import type { 
  StorageAdapter, 
  AgentDetectionEvent, 
  AgentSession 
} from './types';

export class MemoryStorageAdapter implements StorageAdapter {
  private events: Map<string, AgentDetectionEvent[]> = new Map();
  private sessions: Map<string, AgentSession> = new Map();
  private eventTimeline: AgentDetectionEvent[] = [];
  private maxEventsPerSession = 100;
  private maxTimelineEvents = 1000;
  
  async storeEvent(event: AgentDetectionEvent): Promise<void> {
    // Add to session events
    const sessionEvents = this.events.get(event.sessionId) || [];
    sessionEvents.push(event);
    
    // Limit events per session
    if (sessionEvents.length > this.maxEventsPerSession) {
      sessionEvents.shift();
    }
    
    this.events.set(event.sessionId, sessionEvents);
    
    // Add to timeline
    this.eventTimeline.push(event);
    
    // Limit timeline size
    if (this.eventTimeline.length > this.maxTimelineEvents) {
      this.eventTimeline.shift();
    }
  }
  
  async storeSession(session: AgentSession): Promise<void> {
    this.sessions.set(session.sessionId, session);
  }
  
  async getEvents(sessionId: string, limit?: number): Promise<AgentDetectionEvent[]> {
    const events = this.events.get(sessionId) || [];
    if (limit) {
      return events.slice(-limit);
    }
    return events;
  }
  
  async getSession(sessionId: string): Promise<AgentSession | null> {
    return this.sessions.get(sessionId) || null;
  }
  
  async getRecentEvents(limit: number = 50): Promise<AgentDetectionEvent[]> {
    return this.eventTimeline.slice(-limit);
  }
  
  async getActiveSessions(limit: number = 50): Promise<AgentSession[]> {
    const sessions = Array.from(this.sessions.values());
    
    // Sort by last seen (most recent first)
    sessions.sort((a, b) => {
      const timeA = new Date(a.lastSeen).getTime();
      const timeB = new Date(b.lastSeen).getTime();
      return timeB - timeA;
    });
    
    return sessions.slice(0, limit);
  }
  
  async cleanup(before: Date): Promise<void> {
    const cutoff = before.getTime();
    
    // Clean up old events
    for (const [sessionId, events] of this.events.entries()) {
      const filtered = events.filter(e => 
        new Date(e.timestamp).getTime() >= cutoff
      );
      
      if (filtered.length === 0) {
        this.events.delete(sessionId);
      } else {
        this.events.set(sessionId, filtered);
      }
    }
    
    // Clean up old sessions
    for (const [sessionId, session] of this.sessions.entries()) {
      if (new Date(session.lastSeen).getTime() < cutoff) {
        this.sessions.delete(sessionId);
      }
    }
    
    // Clean up timeline
    this.eventTimeline = this.eventTimeline.filter(e =>
      new Date(e.timestamp).getTime() >= cutoff
    );
  }
}